import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../ui/Button';
import { ClientFormTabs } from './ClientFormTabs';
import { Information } from './tabs/Information';
import { Criteria } from './tabs/Criteria';
import { Matching } from './tabs/Matching';
import { useNavigate } from 'react-router-dom';
import { useClientFormTabs } from '../../../hooks/useClientFormTabs';
import { ClientFormProvider } from './contexts/ClientFormContext';
import { clientAPI } from '../../../api/clients';
import { useAuth } from '../../../contexts/AuthContext';

export const ClientForm: React.FC = () => {
  const navigate = useNavigate();
  const { activeTab, setActiveTab } = useClientFormTabs();
  const { user } = useAuth();

  const handleBack = () => {
    if (activeTab === 'criteria') {
      setActiveTab('information');
    } else {
      navigate('/');
    }
  };

  const handleSave = async (formData: any) => {
    console.log('💾 Début de la sauvegarde du client');
    console.log('📋 Données du formulaire:', formData);

    if (!user) {
      alert('Vous devez être connecté pour ajouter un client');
      return;
    }

    // Validation
    if (!formData.civility || !formData.firstName || !formData.lastName || !formData.phone) {
      alert('Veuillez remplir tous les champs obligatoires (civilité, nom, prénom, téléphone)');
      return;
    }

    if (!formData.propertyType || !formData.transactionType) {
      alert('Veuillez sélectionner le type de transaction et le type de bien');
      return;
    }

    try {
      const clientData = {
        civility: formData.civility as 'M.' | 'Mme' | 'Mlle',
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        email: formData.email || undefined,
        address: formData.address,
        wilaya: formData.wilaya,
        city: formData.city,
        notes: formData.notes || undefined,
        transaction_type: formData.transactionType as 'sale' | 'rent' | 'short-rent',
        property_type: formData.propertyType,
        min_budget: parseFloat(formData.minBudget) || 0,
        max_budget: parseFloat(formData.maxBudget) || 0,
        min_area: parseFloat(formData.minArea) || 0,
        min_rooms: parseInt(formData.minRooms) || 1,
        criteria: formData.criteria,
        interest_level: formData.interestLevel as 'very-interested' | 'interested' | 'to-contact',
        agent_id: formData.agent ? parseInt(formData.agent) : undefined,
      };

      console.log('📤 Envoi des données au serveur:', clientData);

      if (!user?.agency_id || !user?.id) {
        throw new Error('Informations d\'authentification manquantes');
      }

      const result = await clientAPI.create(clientData, user.agency_id, user.id);

      console.log('✅ Client créé avec succès:', result);
      alert('Client ajouté avec succès !');
      navigate('/clients-actifs');
    } catch (error) {
      console.error('❌ Erreur lors de la création du client:', error);
      alert('Erreur lors de l\'ajout du client: ' + (error as Error).message);
    }
  };

  return (
    <ClientFormProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center gap-4">
            <Button
              variant="ghost"
              className="p-2"
              onClick={handleBack}
              aria-label="Retour"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Ajouter un client
            </h1>
          </div>

          <div className="rounded-lg bg-white shadow-sm dark:bg-gray-800">
            <ClientFormTabs activeTab={activeTab} onTabChange={setActiveTab} />
            <div className="p-6">
              {activeTab === 'information' ? (
                <Information onNext={() => setActiveTab('criteria')} />
              ) : activeTab === 'criteria' ? (
                <Criteria onNext={() => setActiveTab('matching')} />
              ) : (
                <Matching onSave={(formData) => handleSave(formData)} />
              )}
            </div>
          </div>
        </div>
      </div>
    </ClientFormProvider>
  );
};